import { useEffect, useRef, useState } from "react";
import { GOOGLE_MAPS_KEY } from "../lib/maps";
import { useIsMobile } from "../lib/useIsMobile";

// ════════════════════════════════════════════════════════════════════
//  AddressAutocomplete — reusable Google Places address picker.
//  Mirrors the signup form's autocomplete so EVERY address field in the
//  CRM (operator onboarding, client modal, …) selects a real, validated
//  address instead of free-handing it.
//
//  Controlled: `value` is the street line. `onChange(text)` fires as they
//  type; `onPick({address,city,state,zip,lat,lng})` fires when they choose
//  a suggestion — the parent fills city/state/zip/lat/lng from that.
//
//  Graceful fallback: with no Maps key OR no suggestions for the query, it
//  just behaves as a normal text input so the user is never dead-ended.
// ════════════════════════════════════════════════════════════════════
export function AddressAutocomplete({
  value, onChange, onPick,
  id, className = "", style, placeholder = "Start typing your address…",
  confirmed = false,
}) {
  const isMobile = useIsMobile();
  const [suggestions, setSuggestions] = useState([]);
  const acSession  = useRef(null);
  const justPicked = useRef(false);

  // Debounced autocomplete query (Places API New, REST).
  useEffect(() => {
    if (justPicked.current) { justPicked.current = false; setSuggestions([]); return; }
    const q = (value || "").trim();
    if (q.length < 4 || !GOOGLE_MAPS_KEY) { setSuggestions([]); return; }
    if (!acSession.current) acSession.current = (window.crypto?.randomUUID?.() || String(Math.random()).slice(2));
    const t = setTimeout(async () => {
      try {
        const r = await fetch("https://places.googleapis.com/v1/places:autocomplete", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Goog-Api-Key": GOOGLE_MAPS_KEY },
          body: JSON.stringify({ input: q, includedRegionCodes: ["us"], sessionToken: acSession.current }),
        });
        const d = await r.json();
        const list = Array.isArray(d.suggestions) ? d.suggestions.map(s => s.placePrediction).filter(Boolean).slice(0, 5) : [];
        setSuggestions(list);
      } catch { setSuggestions([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [value]);

  async function pick(p) {
    try {
      const r = await fetch(`https://places.googleapis.com/v1/places/${p.placeId}?sessionToken=${acSession.current || ""}`, {
        headers: { "X-Goog-Api-Key": GOOGLE_MAPS_KEY, "X-Goog-FieldMask": "addressComponents,location,formattedAddress" },
      });
      const place = await r.json();
      const C = {}; (place.addressComponents || []).forEach(c => (c.types || []).forEach(t => { C[t] = c; }));
      const g = (k, w) => C[k] ? (w === "short" ? C[k].shortText : C[k].longText) : "";
      const street = [g("street_number"), g("route")].filter(Boolean).join(" ");
      justPicked.current = true; acSession.current = null;
      setSuggestions([]);
      onPick && onPick({
        address: street || place.formattedAddress || value,
        city:    g("locality") || g("sublocality") || g("postal_town") || "",
        state:   g("administrative_area_level_1", "short") || "",
        zip:     g("postal_code") || "",
        lat:     place.location?.latitude ?? null,
        lng:     place.location?.longitude ?? null,
      });
    } catch { setSuggestions([]); }
  }

  return (
    <div style={{ position: "relative" }}>
      <input
        id={id} className={className} style={style}
        value={value} autoComplete="off"
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {suggestions.length > 0 && (
        // Phone: in-flow (the form grows and scrolls to it with the keyboard up), capped at 4,
        // and picked on click — onMouseDown depends on iOS's emulated mouse events, which are
        // skipped when the finger moves during the tap.
        <div style={isMobile
          ? { position: "static", marginTop: 4, background: "#fff", border: "1px solid #e3e8ef", borderRadius: 8, boxShadow: "0 4px 14px rgba(0,0,0,.08)", overflow: "hidden" }
          : { position: "absolute", top: "100%", left: 0, right: 0, zIndex: 60, background: "#fff", border: "1px solid #e3e8ef", borderRadius: 8, boxShadow: "0 10px 30px rgba(0,0,0,.12)", marginTop: 3, overflow: "hidden" }}>
          {(isMobile ? suggestions.slice(0, 4) : suggestions).map(p => (
            <div key={p.placeId}
              onMouseDown={isMobile ? undefined : () => pick(p)}
              onClick={isMobile ? () => pick(p) : undefined}
              style={{ padding: isMobile ? "11px 13px" : "10px 13px", cursor: "pointer", fontSize: isMobile ? 14 : 13.5, borderBottom: "1px solid #f4f5f7", minHeight: isMobile ? 44 : undefined }}>
              <div style={{ fontWeight: 600, color: "#0f172a" }}>{p.structuredFormat?.mainText?.text || p.text?.text}</div>
              {p.structuredFormat?.secondaryText?.text && <div style={{ fontSize: 12, color: "#64748b" }}>{p.structuredFormat.secondaryText.text}</div>}
            </div>
          ))}
        </div>
      )}
      {confirmed && (value || "").trim() && suggestions.length === 0 && (
        <div style={{ fontSize: 11.5, color: "#166534", fontWeight: 600, marginTop: 5 }}>Address selected ✓</div>
      )}
    </div>
  );
}
